import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import SectionWrapper from 'common/designer-modules/modules/section-wrapper';
import PageHeader from 'common/designer-modules/modules/page-header';
import PageFooter from 'common/designer-modules/modules/footer';
import {
   useDynamicComponentImport,
   VIDEO_RESOURCES_TYPES,
   RECORDED_RESOURCES_TYPES,
   PHOTOSETS_RESOURCES_TYPES,
   STORE_RESOURCES_TYPES,
} from 'common/designer-modules/siteDesigner';

const MMSPage = ({
   isPreview,
   activeEditableSection,
   onClickSection,
   isMobile,
   authUser,
   siteInfo,
   pageSections,
   globalSettings,
   fetchVideos,
   fetchPhotoset,
   initStoreData,
   getStories,
   resources,
   getTheHref,
   sitePages,
}) => {
   const getSectionWrapperProps = (sectionType, section) => {
      let res = {
         isPreview: isPreview,
         isActive: activeEditableSection === section.uuid,
         onClickSection: () => onClickSection(section, 'section'),
         type: sectionType,
         slug: section.uuid,
         activeEditableSection: activeEditableSection,
      }
      return res
   }
   const getSectionProps = (sectionType, currentSectionSettings) => {
      let metas = {}
      if(currentSectionSettings?.metas){
         metas = currentSectionSettings.metas
      }
      let sectionProps = {
         isMobile: isMobile,
         isNextApp: !Boolean(isPreview),
         type: sectionType,
         customSiteDesignerData: { ...currentSectionSettings, ...metas },
         globalSettings: globalSettings,
         hasSingleSection: Object.keys(pageSections)?.length === 1,
         isPreview: isPreview,
         isDisabled: Boolean(activeEditableSection !== currentSectionSettings.uuid && activeEditableSection),
      }
      if(['recorded_content_showcase', 'recorded_livestreams_library'].includes(sectionType)){
         sectionProps = {
            ...sectionProps,
            getFetchApi: fetchVideos,
            ...getMediaSectionProps(currentSectionSettings, RECORDED_RESOURCES_TYPES),
            contentType: 'recorded_stream',
         }
      }
      if(['videos_content_showcase', 'video_library'].includes(sectionType)){

         sectionProps = {
            ...sectionProps,
            getFetchApi: fetchVideos,
            ...getMediaSectionProps(currentSectionSettings, VIDEO_RESOURCES_TYPES),
            contentType: 'video',
         }
      }
      if(['photoset_content_showcase', 'photoset_library'].includes(sectionType)){
         sectionProps = {
            ...sectionProps,
            getFetchApi: fetchPhotoset,
            ...getMediaSectionProps(currentSectionSettings, PHOTOSETS_RESOURCES_TYPES),
            contentType: 'photoset',
         }
      }
      if(['store_content_showcase', 'store'].includes(sectionType)){
         sectionProps = {
            ...sectionProps,
            getFetchApi: initStoreData,
            ...getMediaSectionProps(currentSectionSettings, STORE_RESOURCES_TYPES),
            contentType: 'store',
         }
      }
      if(['stories_content_showcase', 'stories_library'].includes(sectionType)){
         sectionProps = {
            ...sectionProps,
            getFetchApi: getStories,
            ...getMediaSectionProps(currentSectionSettings),
            contentType: 'story',
         }
      }
      return sectionProps;
   }

   const getMediaSectionProps = (currentSectionSettings, type) => {
      let metas = {}
      if(currentSectionSettings?.metas){
         metas = currentSectionSettings.metas
      }
      let currentSectionResources = {};
      if(!isPreview && resources?.[type]){
         currentSectionResources = resources?.[type]
      } else {
         currentSectionResources = resources
      }
      return {
         authUser: authUser,
         siteInfo: siteInfo,
         type: currentSectionSettings.content_type,
         collections: metas.collections,
         contentDefaultSorting: metas.content_default_sorting,
         resources: currentSectionResources,
         getTheHref: getTheHref,
         sitePages: sitePages,
      }
   }
   const sections = pageSections;
   return (
      <div className='flex flex-col w-full items-baseline min-h-full'>
         {
            isPreview && (
               <PageHeader
                  globalSettings={ globalSettings }
                  siteName={ siteInfo.site_long_name }
                  isNextApp={ false }
               />
            )
         }
         <div className='flex flex-col w-full gap-6 mb-6'>
            {
               Object.keys(sections).map((key, index) => {
                  const section = sections?.[key] || {}
                  if(section?.is_deleted || !section?.is_active) return null;
                  const sectionType = section?.type || key;
                  let componentType = sectionType
                  if(['store_content_showcase', 'photoset_content_showcase', 'videos_content_showcase', 'recorded_content_showcase', 'stories_content_showcase'].includes(sectionType)){
                     componentType = 'content_showcase'
                  }
                  if(['stories_library', 'video_library', 'photoset_library', 'recorded_livestreams_library', 'store'].includes(sectionType)){
                     componentType = 'content_library'
                  }
                  return (
                     <SectionWrapper
                        key={ index.toString() }
                        id={ `section_${ section.uuid }` }
                        { ...getSectionWrapperProps(sectionType, section) }
                     >
                        <SectionComponent
                           sectionType={ sectionType }
                           componentType={ componentType }
                           componentProps={ {
                              ...getSectionProps(sectionType, section),
                           } }
                           isPreview={ isPreview }
                        />
                     </SectionWrapper>
                  )
               })
            }
         </div>
         <PageFooter />
      </div>
   )

}
const SectionComponent = ({
   componentProps = {},
   componentType,
   isPreview,
}) => {
   const sectionPath = componentType ? componentType.replaceAll('_', '-') : '';
   const { Component, isLoadComponent, notFoundComponent } = useDynamicComponentImport(sectionPath, 'common/designer-modules/page-template', isPreview); //common/designer-modules/page-template
   return (
      <Suspense
         fallback={
            <div
               className='loading-bg bg-placeholder rounded-lg w-full my-auto h-full'
            />
         }
      >
         {
            notFoundComponent && (
               <>
                  ----
               </>
            )
         }
         {
            !isLoadComponent && !notFoundComponent && (
               <Component
                  { ...componentProps }
               />
            )
         }
         {
            (isLoadComponent || !Component) && (
               <div
                  className='loading-bg bg-placeholder rounded-lg w-full my-auto h-full'
               />
            )
         }
      </Suspense>
   )
}
MMSPage.propTypes = {
   fetchVideos: PropTypes.func,
   fetchPhotoset: PropTypes.func,
   getStories: PropTypes.func,
   initStoreData: PropTypes.func,
   isPreview: PropTypes.bool,
   activeEditableSection: PropTypes.string,
   onClickSection: PropTypes.func,
   isMobile: PropTypes.bool,
   authUser: PropTypes.object,
   siteInfo: PropTypes.object,
   globalSettings: PropTypes.object,
   pageSections: PropTypes.object,
   resources: PropTypes.object,
   getTheHref: PropTypes.func,
   sitePages: PropTypes.array,
};
MMSPage.defaultProps = {
   isEditable: false,
}
SectionComponent.propTypes = {
   componentType: PropTypes.string,
   componentProps: PropTypes.object,
   isPreview: PropTypes.object,
}
export default MMSPage;


